import { Backdrop, CircularProgress } from '@mui/material';
import { Navigate, useLocation, useParams } from 'react-router';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { BASE } from 'common/src/routes/BaseRoutes';

import Error from '../../pages/Error';
import useAuth from '../../auth/useAuth';

interface ImageProps {
  fetchImageFile: (id: string) => Promise<Blob>;
  title: string;
}

export default function ImageViewer({ fetchImageFile, title }: ImageProps) {
  const { isAuthenticated } = useAuth();
  const { id } = useParams();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const pageNumber = query.get('page');
  const authenticated = isAuthenticated();

  const {
    data: imageFile,
    isFetching,
    isError,
  } = useQuery({
    queryKey: [title, id],
    queryFn: async () => await fetchImageFile(id ?? ''),
    initialData: undefined,
    enabled: authenticated,
  });

  const fileSrc = useMemo(() => {
    return imageFile ? URL.createObjectURL(imageFile) : null;
  }, [imageFile]);

  if (!authenticated) {
    return (
      <Navigate
        to={BASE.AUTH.LOGIN}
        state={{
          from: {
            pathname: location.pathname,
            search: location.search,
          },
        }}
        replace
      />
    );
  }

  if (isError) {
    return <Error />;
  }

  if (isFetching) {
    return (
      <Backdrop open={isFetching}>
        <CircularProgress />
      </Backdrop>
    );
  }

  if (imageFile) {
    return (
      <div>
        {!imageFile.type.includes('image') ? (
          <iframe
            title={title}
            src={pageNumber ? `${fileSrc}#page=${pageNumber}` : fileSrc}
            style={{ width: '100vw', height: '100vh' }}
          />
        ) : (
          <img alt={title} src={fileSrc ?? ''} style={{ maxWidth: '100%' }} />
        )}
      </div>
    );
  }

  return null;
}
